/* Load decorative assets near their section; keep native commerce and video controls. */
(() => {
  if (window.EvolvedDeferredAssets) return;
  const selector = '[data-evolved-flags], [data-evolved-background], [data-evolved-poster]';
  const pending = new Map();
  const loadedStyles = new Set();
  const drawerSelector = 'navigation-drawer, cart-drawer';
  const drawerObservers = new Map();

  const loadDrawerImages = (drawer) => {
    if (!drawer?.matches(drawerSelector)) return;
    drawer.querySelectorAll('img[data-evolved-drawer-src]').forEach((image) => {
      if (image.dataset.evolvedDrawerSrcset) image.srcset = image.dataset.evolvedDrawerSrcset;
      image.src = image.dataset.evolvedDrawerSrc;
      delete image.dataset.evolvedDrawerSrcset;
      delete image.dataset.evolvedDrawerSrc;
    });
  };

  const initDrawers = (root) => {
    const drawers = Array.from(root.querySelectorAll(drawerSelector));
    if (root.matches?.(drawerSelector)) drawers.push(root);
    drawers.forEach((drawer) => {
      if (drawer.hasAttribute('open')) loadDrawerImages(drawer);
      if (drawerObservers.has(drawer)) return;
      const observer = new MutationObserver((records) => {
        if (drawer.hasAttribute('open') && records.some(record => record.addedNodes.length)) {
          loadDrawerImages(drawer);
        }
      });
      observer.observe(drawer, { childList: true, subtree: true });
      drawerObservers.set(drawer, observer);
    });
  };

  const onDrawerIntent = (event) => {
    const control = event.target.closest?.('[aria-controls]');
    if (control) loadDrawerImages(document.getElementById(control.getAttribute('aria-controls')));
  };
  document.addEventListener('pointerdown', onDrawerIntent, { capture: true, passive: true });
  document.addEventListener('focusin', onDrawerIntent);
  document.addEventListener('toggle', (event) => {
    if (event.target.matches?.(drawerSelector) && event.target.hasAttribute('open')) loadDrawerImages(event.target);
  }, true);
  document.addEventListener('dialog:after-show', (event) => loadDrawerImages(event.target));

  const load = (element) => {
    const { evolvedFlags, evolvedBackground, evolvedPoster } = element.dataset;
    if (evolvedFlags && !loadedStyles.has(evolvedFlags)) {
      loadedStyles.add(evolvedFlags);
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = evolvedFlags;
      link.dataset.evolvedFlagsStyles = '';
      link.addEventListener('error', () => {
        loadedStyles.delete(evolvedFlags);
        link.remove();
      }, { once: true });
      document.head.append(link);
    }
    if (evolvedBackground) {
      element.style.backgroundImage = `url(${JSON.stringify(evolvedBackground)})`;
      delete element.dataset.evolvedBackground;
    }
    if (evolvedPoster) {
      element.setAttribute('poster', evolvedPoster);
      delete element.dataset.evolvedPoster;
    }
  };

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const element = pending.get(entry.target);
      if (element) load(element);
      observer.unobserve(entry.target);
      pending.delete(entry.target);
    });
  }, { rootMargin: '400px 0px' }) : null;

  const init = (root) => {
    initDrawers(root);
    root.querySelectorAll(selector).forEach((element) => {
      // A hidden country menu must not fetch the atlas. Watch only its selected flag.
      const target = element.hasAttribute('data-evolved-flags')
        ? element.querySelector(':scope > button .country-flags') : element;
      if (!observer) { load(element); return; }
      if (!target || pending.has(target)) return;
      pending.set(target, element);
      observer.observe(target);
    });
  };

  const onCountryIntent = (event) => {
    const element = event.target.closest?.('[data-evolved-flags]');
    if (element) load(element);
  };
  document.addEventListener('pointerdown', onCountryIntent, { capture: true, passive: true });
  document.addEventListener('focusin', onCountryIntent);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    drawerObservers.forEach((observer, drawer) => {
      if (drawer.isConnected && !event.target.contains(drawer)) return;
      observer.disconnect();
      drawerObservers.delete(drawer);
    });
    pending.forEach((element, target) => {
      if (!event.target.contains(element)) return;
      observer?.unobserve(target);
      pending.delete(target);
    });
  });
  // Featured tabs own their own image lifecycle. Other cards load hover media on intent.
  const hoverMedia = matchMedia('(hover: hover) and (pointer: fine)');
  const cardImages = new WeakSet();
  const loadCardMedia = (event) => {
    if (event.type === 'pointerover' && (!hoverMedia.matches || event.pointerType === 'touch')) return;
    const card = event.target.closest?.('product-card');
    if (!card || card.closest('evolved-featured-collection-tabs') ||
        (event.type === 'pointerover' && card.contains(event.relatedTarget))) return;
    card.querySelectorAll('img[data-evolved-image-pending="secondary"]').forEach((image) => {
      if (cardImages.has(image)) return;
      cardImages.add(image);
      const ready = () => {
        if (image.naturalWidth > 0) image.removeAttribute('data-evolved-image-pending');
      };
      image.addEventListener('load', ready, { once: true });
      image.addEventListener('error', () => cardImages.delete(image), { once: true });
      if (image.dataset.evolvedSrcset) image.srcset = image.dataset.evolvedSrcset;
      if (image.dataset.evolvedSrc) image.src = image.dataset.evolvedSrc;
      if (image.complete) ready();
    });
  };
  document.addEventListener('pointerover', loadCardMedia, { passive: true });
  document.addEventListener('focusin', loadCardMedia);
  window.EvolvedDeferredAssets = { init };
  init(document);
})();
